import { queryClient, queryKeys } from '@/lib/query-client';
import {
  showErrorToast,
  showSuccessToast,
  showWarningToast,
} from '@/shared/notifications/toast-utils';
import { useMutation, useQuery } from '@tanstack/react-query';
import { shiftsService } from '../api/shiftsService';
import type {
  AssignShiftInput,
  AssignShiftToTeamInput,
  CreateShiftInput,
  RemoveTeamShiftInput,
  UpdateShiftInput,
} from '../types/shift';

export function useShifts(params?: {
  limit?: number;
  skip?: number;
  sortColumn?: string;
  sortOrder?: string;
  search?: string;
}) {
  return useQuery({
    queryKey: queryKeys.shifts.list(params as Record<string, unknown>),
    queryFn: () => shiftsService.listShifts(params),
  });
}

export function useShift(id: number | null) {
  return useQuery({
    queryKey: queryKeys.shifts.detail(String(id)),
    queryFn: () => shiftsService.getShift(id!),
    enabled: id !== null,
  });
}

export function useCreateShift() {
  return useMutation({
    mutationFn: (data: CreateShiftInput) => shiftsService.createShift(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.shifts.lists() });
    },
    meta: {
      resourceName: 'Shift',
      action: 'create',
      // The create modal catches errors and shows its own contextual toast
      // (e.g. the duplicate-title 409). Skip the global handler to avoid a double toast.
      skipErrorToast: true,
    },
  });
}

export function useUpdateShift(id: number) {
  return useMutation({
    mutationFn: (data: UpdateShiftInput) => shiftsService.updateShift(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.shifts.lists() });
      queryClient.invalidateQueries({ queryKey: queryKeys.shifts.detail(String(id)) });
    },
    meta: {
      resourceName: 'Shift',
      action: 'update',
      // The edit modal catches errors and shows its own contextual toast
      // (e.g. the duplicate-title 409). Skip the global handler to avoid a double toast.
      skipErrorToast: true,
    },
  });
}

export function useDeleteShift() {
  return useMutation({
    mutationFn: (id: number) => shiftsService.deleteShift(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.shifts.lists() });
    },
    meta: {
      resourceName: 'Shift',
      action: 'delete',
    },
  });
}

export function useAssignShift() {
  return useMutation({
    mutationFn: (data: AssignShiftInput) => shiftsService.assignShift(data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.shifts.lists() });
      queryClient.invalidateQueries({
        queryKey: queryKeys.shifts.detail(String(variables.shiftId)),
      });
    },
    meta: {
      successTitle: 'Shift Assigned',
      successMessage: 'Shift assigned to user successfully',
    },
  });
}

export function useDeleteAssignment() {
  return useMutation({
    mutationFn: (id: number) => shiftsService.deleteAssignment(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.shifts.lists() });
      // Invalidate all shift details since we don't know which shift this assignment belonged to
      queryClient.invalidateQueries({ queryKey: queryKeys.shifts.all });
    },
    meta: {
      successTitle: 'Assignment Removed',
      successMessage: 'Shift assignment removed successfully',
    },
  });
}

export function useTeamShifts(teamId: number | null) {
  return useQuery({
    queryKey: queryKeys.shifts.teamShifts(teamId ?? 0),
    queryFn: () => shiftsService.listTeamShifts(teamId!),
    enabled: teamId !== null,
  });
}

export function useRemoveTeamShift() {
  return useMutation({
    mutationFn: (data: RemoveTeamShiftInput) => shiftsService.removeTeamShift(data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.shifts.teamShifts(variables.teamId) });
      queryClient.invalidateQueries({ queryKey: queryKeys.shifts.lists() });
      queryClient.invalidateQueries({
        queryKey: queryKeys.shifts.detail(String(variables.shiftId)),
      });
    },
    meta: {
      successTitle: 'Shift Removed',
      successMessage: 'Shift assignment removed from team successfully',
    },
  });
}

export function useAssignShiftToTeam() {
  return useMutation({
    mutationFn: (data: AssignShiftToTeamInput) => shiftsService.assignShiftToTeam(data),
    onSuccess: (result, variables) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.shifts.lists() });
      queryClient.invalidateQueries({
        queryKey: queryKeys.shifts.detail(String(variables.shiftId)),
      });
      queryClient.invalidateQueries({
        queryKey: queryKeys.shifts.teamShifts(variables.teamId),
      });

      const { summary } = result;
      const issues = [
        summary.conflicts > 0 ? `${summary.conflicts} already had an overlapping shift` : null,
        summary.failed > 0 ? `${summary.failed} failed` : null,
      ]
        .filter(Boolean)
        .join(', ');

      if (summary.successful === 0) {
        // Nothing was assigned — every member was a conflict/failure. This is not a success.
        showErrorToast(
          'No Members Assigned',
          `Shift not assigned: ${issues || 'no eligible team members'}. Remove the clashing assignment(s) and try again.`,
        );
      } else if (summary.conflicts > 0 || summary.failed > 0) {
        showWarningToast(
          'Partial Assignment',
          `${summary.successful} of ${summary.total} assigned — ${issues}.`,
        );
      } else {
        showSuccessToast('Team Assigned', `Shift assigned to all ${summary.successful} members.`);
      }
    },
    meta: {
      skipSuccessToast: true, // Custom handling in onSuccess
    },
  });
}
